export const useAuthenticatedFetch = () => {
  const authStore = useAuthStore();
  const router = useRouter();

  const authenticatedFetch = async <T>(
    url: string,
    options: any = {}
  ): Promise<T> => {
    // Ensure auth store is initialized before making requests
    // Initialize if no user OR if user exists but no token (token expired scenario)
    if ((!authStore.user || !authStore.token) && !authStore.isLoading) {
      await authStore.initAuth();
    }

    // If still not authenticated after initialization, throw error
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated');
    }

    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (authStore.token) {
      headers.Authorization = `Bearer ${authStore.token}`;
      console.log('useAuthenticatedFetch: Adding auth header, token exists:', !!authStore.token);
      console.log('useAuthenticatedFetch: Headers object:', headers);
    } else {
      throw new Error('No authentication token available');
    }

    console.log('useAuthenticatedFetch: Making request to', url, 'with options:', { ...options, headers });

    try {
      const response = await $fetch<T>(url, {
        ...options,
        headers,
      });
      return response;
    } catch (err: any) {
      const status = err?.response?.status
      const message = err?.response?._data?.message || err?.data?.message
      const shouldAttemptRefresh = status === 401 || status === 403
        || (typeof message === 'string' && message.toLowerCase().includes('not authenticated'))

      if (shouldAttemptRefresh) {
        // Try to refresh the token if we have a refresh token
        if (authStore.refreshToken) {
          const refreshed = await authStore.refreshAuthToken();
          if (refreshed) {
            // Retry the original request with the new token
            headers.Authorization = `Bearer ${authStore.token}`;
            try {
              const response = await $fetch<T>(url, {
                ...options,
                headers,
              });
              return response;
            } catch (retryErr) {
              // If retry fails, proceed with logout
              console.error('Request retry failed after token refresh', retryErr);
            }
          }
        }
        
        // If refresh failed or no refresh token, clear auth data
        authStore.$reset?.() // If using Pinia, or manually clear token/user
        // Redirect to login
        router.push('/login');
      }
      throw err;
    }
  };

  return {
    authenticatedFetch,
  };
};
